// Second breakdown fixture: the breakdown location is the tail of the
// interrupted sequence (`toStation`), and there is NO post-incident driving
// sequence starting at the virtual station — only `outboundTravelingWorkAssignment`
// inside the breakdown record. That means `insertVirtualStations` only sees
// an inbound sequence (arrival) with no matching outbound (departure), so
// the resulting virtual station carries `departure: null`. This exercises
// the "one-sided timestamp" branch that would otherwise push the badge to
// the row's edge via .edge.right.
//
// Anonymised: real crew names / emails replaced with Norse-mythology placeholders.

const RIDE_ID = '6be8bb6a-8a67-4704-80a7-b0b9df0fcd6a';

const PRIMARY_CREW_ID = '47f0d164-2e8b-47c9-b425-fe9af2aa99a8';
const SECONDARY_CREW_ID = '78a14844-be19-4497-8c2d-1ef0ad65d89b';

const PRIMARY_CREW = { fullName: 'Ragnar Lothbrok' };
const SECONDARY_CREW = { fullName: 'Lagertha' };

const ELP6 = '9a1ebcb4-a980-4bc0-890f-41c5525c8e16';
const FTS6 = '539bd509-a3e5-4f57-badf-e675625a8b0a';
const JCT6 = 'da310228-a33b-407c-a9ec-5f8dc2263701';
const KRV6 = 'c4b2204b-b458-446d-a46c-45209ea4cc2b';
const SNT6 = '049a9447-2544-4f5a-b4dd-3470eb261c59';
const HOU6 = '70fe4a43-595a-4723-8b23-1ed1247b62b1';
const XAL6 = '4033e13a-0763-424e-95df-ced77424f221';

const BREAKDOWN_RECORD = {
	workAssignmentSequenceGroupId: 'ae4205a0-cbb6-4e2a-99ee-0c5744fd5072',
	incidentTimestamp: '2026-08-05T14:55:00Z',
	reason: 'busFailure',
	station: {
		id: XAL6,
		code: 'XAL6',
		name: 'GHDummy AL Breakdowns Only',
		__typename: 'Station',
	},
	note: null,
	deadhead: null,
	inboundTravelingWorkAssignment: null,
	replacementCrewMember: {
		id: PRIMARY_CREW_ID,
		fullName: PRIMARY_CREW.fullName,
		driverCard: 'DC-RAGNAR-01',
		__typename: 'CrewMember',
	},
	parallelRide: {
		id: '0ed998f4-2f03-443f-8d9d-5093c69a4bdd',
		tripNumber: '1517',
		lineCode: 'US0601',
		departure: '2026-08-02T22:00:00Z',
		stations: [
			{ id: 'p-1', name: 'Nashville Bus Station', code: 'NAS6', __typename: 'RideStation' },
			{ id: 'p-2', name: 'Jackson (Mega Mart)', code: 'JXT6', __typename: 'RideStation' },
			{ id: 'p-3', name: 'Memphis Bus Station', code: 'MEM6', __typename: 'RideStation' },
			{ id: 'p-4', name: "Bryant-Little Rock (Gassy's)", code: 'BRT6', __typename: 'RideStation' },
			{ id: 'p-5', name: 'Texarkana (Travel Stop)', code: 'TEX6', __typename: 'RideStation' },
			{
				id: 'p-6',
				name: 'Sulphur Springs (Travel Center)',
				code: 'SPP6',
				__typename: 'RideStation',
			},
			{ id: 'p-7', name: 'Dallas Bus Station', code: 'DLD6', __typename: 'RideStation' },
		],
		__typename: 'Ride',
	},
	postIncidentDrivingAssignments: null,
	outboundTravelingWorkAssignment: {
		id: 'a0e26922-c5df-45d3-93a8-ea5d63ae3c22',
		crewMember: {
			id: PRIMARY_CREW_ID,
			fullName: PRIMARY_CREW.fullName,
			__typename: 'Driver',
		},
		fromStation: { name: 'GHDummy AL Breakdowns Only', code: 'XAL6', __typename: 'Station' },
		toStation: { name: 'San Antonio Bus Station', code: 'SNT6', __typename: 'Station' },
		fromTimestamp: '2026-08-05T15:56:00Z',
		toTimestamp: '2026-08-05T16:58:00Z',
		__typename: 'WorkAssignment',
	},
	createdAt: '2026-08-03T11:53:09.263351Z',
	updatedAt: '2026-08-03T11:53:09.263354Z',
	updatedBy: 'test@example.com',
	__typename: 'Breakdown',
};

export const RIDE_DETAILS_BREAKDOWN_NO_POST_INCIDENT_MOCK = {
	id: RIDE_ID,
	tripNumber: '1422',
	status: 'on_sale',
	departure: '2026-08-04T18:45:00Z',
	lineCode: 'US1001',
	stations: [
		{
			id: ELP6,
			code: 'ELP6',
			name: 'El Paso Bus Station (W San Antonio St)',
			timezone: 'America/Denver',
			departure: '2026-08-04T18:45:00Z',
			arrival: null,
			city: {
				name: 'El Paso, TX',
				id: 'd2250517-156d-4e98-9db4-195b8455b985',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
		{
			id: FTS6,
			code: 'FTS6',
			name: 'Fort Stockton (Stripes)',
			timezone: 'America/Chicago',
			departure: '2026-08-05T00:00:00Z',
			arrival: '2026-08-04T23:15:00Z',
			city: {
				name: 'Fort Stockton, TX',
				id: 'bf64fa43-01e6-4a54-8424-3e2fd826c28d',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
		{
			id: JCT6,
			code: 'JCT6',
			name: 'Junction (Short Stop)',
			timezone: 'America/Chicago',
			departure: '2026-08-05T03:20:00Z',
			arrival: '2026-08-05T03:05:00Z',
			city: {
				name: 'Junction, TX',
				id: 'abd54701-8dd9-47c6-b317-7f4afa2ced00',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
		{
			id: KRV6,
			code: 'KRV6',
			name: 'Kerrville Bus Stop',
			timezone: 'America/Chicago',
			departure: '2026-08-05T04:20:00Z',
			arrival: '2026-08-05T04:15:00Z',
			city: {
				name: 'Kerrville, TX',
				id: '9431c75c-4c9a-41d9-9718-1edaf61de877',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
		{
			id: SNT6,
			code: 'SNT6',
			name: 'San Antonio Bus Station',
			timezone: 'America/Chicago',
			departure: '2026-08-05T06:05:00Z',
			arrival: '2026-08-05T05:25:00Z',
			city: {
				name: 'San Antonio, TX',
				id: 'ff905672-4924-47f4-9e1c-ef551b1f88ee',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
		{
			id: HOU6,
			code: 'HOU6',
			name: 'Houston Bus Station (Greyhound)',
			timezone: 'America/Chicago',
			departure: null,
			arrival: '2026-08-05T09:30:00Z',
			city: {
				name: 'Houston, TX',
				id: '364040d5-ebfc-4628-b7b0-13e2377203c6',
				__typename: 'City',
			},
			__typename: 'RideStation',
		},
	],
	workAssignmentSequences: [
		{
			isMultiRide: false,
			sequenceGroupId: 'ae4205a0-cbb6-4e2a-99ee-0c5744fd5072',
			rideId: RIDE_ID,
			crewMember: {
				id: PRIMARY_CREW_ID,
				fullName: PRIMARY_CREW.fullName,
				__typename: 'CrewMember',
			},
			breakdown: BREAKDOWN_RECORD,
			fromStation: {
				id: ELP6,
				name: 'El Paso Bus Station (W San Antonio St)',
				__typename: 'Station',
			},
			toStation: { id: SNT6, name: 'San Antonio Bus Station', __typename: 'Station' },
			fromTimestamp: '2026-08-04T18:45:00Z',
			toTimestamp: '2026-08-05T05:25:00Z',
			activityType: 'driving',
			status: 'rejected',
			workAssignments: [
				{
					crewMember: {
						id: PRIMARY_CREW_ID,
						fullName: PRIMARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'rejected',
					version: 3,
					id: 'f462d10c-3661-4564-acc5-e1346272c138',
					fromStation: {
						id: ELP6,
						name: 'El Paso Bus Station (W San Antonio St)',
						__typename: 'Station',
					},
					toStation: {
						id: FTS6,
						name: 'Fort Stockton (Stripes)',
						__typename: 'Station',
					},
					__typename: 'WorkAssignment',
				},
				{
					crewMember: {
						id: PRIMARY_CREW_ID,
						fullName: PRIMARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'rejected',
					version: 3,
					id: '7a9ec0a5-b4d7-446b-9d55-05fdcde9020d',
					fromStation: { id: FTS6, name: 'Fort Stockton (Stripes)', __typename: 'Station' },
					toStation: { id: JCT6, name: 'Junction (Short Stop)', __typename: 'Station' },
					__typename: 'WorkAssignment',
				},
				{
					crewMember: {
						id: PRIMARY_CREW_ID,
						fullName: PRIMARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'rejected',
					version: 3,
					id: '4923cc0a-a3cd-44fd-aae1-d392b05ed534',
					fromStation: { id: JCT6, name: 'Junction (Short Stop)', __typename: 'Station' },
					toStation: { id: KRV6, name: 'Kerrville Bus Stop', __typename: 'Station' },
					__typename: 'WorkAssignment',
				},
				{
					crewMember: {
						id: PRIMARY_CREW_ID,
						fullName: PRIMARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'rejected',
					version: 3,
					id: 'f8c23df4-3e3f-48ec-93cd-a85f43d6c58f',
					fromStation: { id: KRV6, name: 'Kerrville Bus Stop', __typename: 'Station' },
					toStation: { id: SNT6, name: 'San Antonio Bus Station', __typename: 'Station' },
					__typename: 'WorkAssignment',
				},
			],
			run: { name: '7000', id: '7bb8df66-3d00-4366-8339-0b0f599ad166', __typename: 'Run' },
			job: {
				id: 'e776c86f-7569-40c5-84d1-cbf4b1d96e5d',
				code: 'B',
				runId: '7bb8df66-3d00-4366-8339-0b0f599ad166',
				__typename: 'Job',
			},
			extraActivities: [
				{
					fromTimestamp: '2026-08-04T18:05:00Z',
					toTimestamp: '2026-08-04T18:40:00Z',
					id: '870c8d59-56ae-4bf1-a238-3ead0f968fd3',
					version: 3,
					status: 'rejected',
					fromStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					toStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					activityType: 'signOn',
					__typename: 'WorkAssignment',
				},
				{
					fromTimestamp: '2026-08-05T05:25:00Z',
					toTimestamp: '2026-08-05T05:40:00Z',
					id: 'aea8ce66-a1a9-43c1-baf3-7c689880032e',
					version: 3,
					status: 'rejected',
					fromStation: { id: SNT6, code: 'SNT6', __typename: 'Station' },
					toStation: { id: SNT6, code: 'SNT6', __typename: 'Station' },
					activityType: 'signOff',
					__typename: 'WorkAssignment',
				},
				{
					fromTimestamp: '2026-08-04T18:40:00Z',
					toTimestamp: '2026-08-04T18:45:00Z',
					id: 'af6c99c9-59e1-48b2-98bd-f55d2de443e3',
					version: 3,
					status: 'rejected',
					fromStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					toStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					activityType: 'garaging',
					__typename: 'WorkAssignment',
				},
			],
			__typename: 'WorkAssignmentSequence',
		},
		{
			isMultiRide: false,
			sequenceGroupId: 'ae4205a0-cbb6-4e2a-99ee-0c5744fd5072',
			rideId: RIDE_ID,
			crewMember: {
				id: PRIMARY_CREW_ID,
				fullName: PRIMARY_CREW.fullName,
				__typename: 'CrewMember',
			},
			breakdown: BREAKDOWN_RECORD,
			fromStation: {
				id: ELP6,
				name: 'El Paso Bus Station (W San Antonio St)',
				__typename: 'Station',
			},
			toStation: { id: XAL6, name: 'GHDummy AL Breakdowns Only', __typename: 'Station' },
			fromTimestamp: '2026-08-04T18:45:00Z',
			toTimestamp: '2026-08-05T14:55:00Z',
			activityType: 'driving',
			status: 'confirmed',
			workAssignments: [
				{
					crewMember: {
						id: PRIMARY_CREW_ID,
						fullName: PRIMARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'confirmed',
					version: 1,
					id: '9c7a8810-d1d2-460b-8606-631b22a44d35',
					fromStation: {
						id: ELP6,
						name: 'El Paso Bus Station (W San Antonio St)',
						__typename: 'Station',
					},
					toStation: {
						id: XAL6,
						name: 'GHDummy AL Breakdowns Only',
						__typename: 'Station',
					},
					__typename: 'WorkAssignment',
				},
			],
			run: null,
			job: null,
			extraActivities: [
				{
					fromTimestamp: '2026-08-04T18:05:00Z',
					toTimestamp: '2026-08-04T18:40:00Z',
					id: '870c8d59-56ae-4bf1-a238-3ead0f968fd3',
					version: 3,
					status: 'rejected',
					fromStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					toStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					activityType: 'signOn',
					__typename: 'WorkAssignment',
				},
				{
					fromTimestamp: '2026-08-05T05:25:00Z',
					toTimestamp: '2026-08-05T05:40:00Z',
					id: 'aea8ce66-a1a9-43c1-baf3-7c689880032e',
					version: 3,
					status: 'rejected',
					fromStation: { id: SNT6, code: 'SNT6', __typename: 'Station' },
					toStation: { id: SNT6, code: 'SNT6', __typename: 'Station' },
					activityType: 'signOff',
					__typename: 'WorkAssignment',
				},
				{
					fromTimestamp: '2026-08-04T18:40:00Z',
					toTimestamp: '2026-08-04T18:45:00Z',
					id: 'af6c99c9-59e1-48b2-98bd-f55d2de443e3',
					version: 3,
					status: 'rejected',
					fromStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					toStation: { id: ELP6, code: 'ELP6', __typename: 'Station' },
					activityType: 'garaging',
					__typename: 'WorkAssignment',
				},
			],
			__typename: 'WorkAssignmentSequence',
		},
		{
			isMultiRide: true,
			sequenceGroupId: '666d90cc-f748-4f9b-b323-cd3f02f99a14',
			rideId: RIDE_ID,
			crewMember: {
				id: SECONDARY_CREW_ID,
				fullName: SECONDARY_CREW.fullName,
				__typename: 'CrewMember',
			},
			breakdown: null,
			fromStation: { id: SNT6, name: 'San Antonio Bus Station', __typename: 'Station' },
			toStation: {
				id: HOU6,
				name: 'Houston Bus Station (Greyhound)',
				__typename: 'Station',
			},
			fromTimestamp: '2026-08-05T06:05:00Z',
			toTimestamp: '2026-08-05T09:30:00Z',
			activityType: 'driving',
			status: 'confirmed',
			workAssignments: [
				{
					crewMember: {
						id: SECONDARY_CREW_ID,
						fullName: SECONDARY_CREW.fullName,
						__typename: 'Driver',
					},
					status: 'confirmed',
					version: 2,
					id: '7257424f-83e7-4918-b233-9175c313574b',
					fromStation: { id: SNT6, name: 'San Antonio Bus Station', __typename: 'Station' },
					toStation: {
						id: HOU6,
						name: 'Houston Bus Station (Greyhound)',
						__typename: 'Station',
					},
					__typename: 'WorkAssignment',
				},
			],
			run: { name: '3506', id: '19eea52f-f21c-4560-9b27-851b747a640c', __typename: 'Run' },
			job: {
				id: '36db6658-b748-4d69-8c3b-f9ba024e55f5',
				code: 'B',
				runId: '19eea52f-f21c-4560-9b27-851b747a640c',
				__typename: 'Job',
			},
			extraActivities: [
				{
					fromTimestamp: '2026-08-05T09:35:00Z',
					toTimestamp: '2026-08-05T09:50:00Z',
					id: '1991082f-4b29-4ea5-9eff-28dcedaddee6',
					version: 2,
					status: 'confirmed',
					fromStation: { id: HOU6, code: 'HOU6', __typename: 'Station' },
					toStation: { id: HOU6, code: 'HOU6', __typename: 'Station' },
					activityType: 'signOff',
					__typename: 'WorkAssignment',
				},
				{
					fromTimestamp: '2026-08-05T09:30:00Z',
					toTimestamp: '2026-08-05T09:35:00Z',
					id: 'f8bcd2de-143f-4855-bf1b-f1e78f71658a',
					version: 2,
					status: 'confirmed',
					fromStation: { id: HOU6, code: 'HOU6', __typename: 'Station' },
					toStation: { id: HOU6, code: 'HOU6', __typename: 'Station' },
					activityType: 'garaging',
					__typename: 'WorkAssignment',
				},
			],
			__typename: 'WorkAssignmentSequence',
		},
	],
	vehicleAssignments: [
		{
			mobilityPartnerName: 'Dallas GLI',
			plateNumber: 'K070753',
			busNumber: '86765',
			vehicleType: 'bus',
			fromStationId: ELP6,
			toStationId: HOU6,
			__typename: 'VehicleAssignment',
		},
	],
	activityTypeConfigs: [
		{ activityType: 'hosting', limit: 0, __typename: 'ActivityTypeConfig' },
		{ activityType: 'driving', limit: 1, __typename: 'ActivityTypeConfig' },
	],
	runs: [
		{
			id: '19eea52f-f21c-4560-9b27-851b747a640c',
			name: '3506',
			description: '',
			effectiveDate: '2026-06-25',
			expireDate: '2026-08-06',
			concessionOwners: [
				{
					id: 'cf32eb76-0ade-43dd-a399-fbace9165325',
					name: 'Greyhound Lines, Inc.',
					__typename: 'ConcessionOwner',
				},
			],
			runCity: {
				id: '364040d5-ebfc-4628-b7b0-13e2377203c6',
				name: 'Houston, TX',
				timezone: 'America/Chicago',
				__typename: 'City',
			},
			__typename: 'Run',
		},
		{
			id: '7bb8df66-3d00-4366-8339-0b0f599ad166',
			name: '7000',
			description: '',
			effectiveDate: '2026-06-25',
			expireDate: '2026-08-06',
			concessionOwners: [
				{
					id: 'cf32eb76-0ade-43dd-a399-fbace9165325',
					name: 'Greyhound Lines, Inc.',
					__typename: 'ConcessionOwner',
				},
			],
			runCity: {
				id: 'ff905672-4924-47f4-9e1c-ef551b1f88ee',
				name: 'San Antonio, TX',
				timezone: 'America/Chicago',
				__typename: 'City',
			},
			__typename: 'Run',
		},
	],
	jobs: [
		{
			id: 'e776c86f-7569-40c5-84d1-cbf4b1d96e5d',
			code: 'B',
			runId: '7bb8df66-3d00-4366-8339-0b0f599ad166',
			distanceKm: 897.2,
			durationSeconds: 34500,
			__typename: 'Job',
		},
		{
			id: '36db6658-b748-4d69-8c3b-f9ba024e55f5',
			code: 'B',
			runId: '19eea52f-f21c-4560-9b27-851b747a640c',
			distanceKm: 637.2,
			durationSeconds: 26400,
			__typename: 'Job',
		},
	],
	__typename: 'Ride',
};
